import { CenteredSpin, IconButton, TimeRangePicker } from '../../../ui'
import { FC, useEffect, useMemo, useState } from 'react'

import style from './style.m.less'
import { Input } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { getDictionaries, selectDictionariesOptions } from '../../../../store/dictionaries'
import TagSelector from '../TagSelector/TagSelector'
import { ELoading } from '../../../../store/dictionaries/interfaces'
import moment from 'moment'
import { DropdownSelector } from './components'
import { useTranslation } from 'react-i18next'

export interface IValue {
    renewableEnergyType: number[]
    totalPowerFrom: number
    totalPowerTo: number
    qualificDate?: [moment.Moment, moment.Moment] | [null, null]
}

interface IFilters {
    values: IValue
    onChange(values: IValue): void
    onClose?: () => void
    names?: any
    titles?: any
    showQualificDate?: boolean
    disableOutside?: (state: boolean) => void
}

const UNIT_IDS = {
    MW: 'МВт',
    KW: 'КВт',
} as const

const Filters: FC<IFilters> = ({
    values,
    onChange,
    onClose,
    names = {},
    titles = {},
    showQualificDate = false,
    disableOutside,
}) => {
    const { t, i18n } = useTranslation()
    const locale = useMemo(() => {
        const lang = i18n.language.toLowerCase()
        return lang.includes('ru') ? 'ru' : 'en'
    }, [i18n.language])

    const unitTypes = useMemo(
        () => [
            { value: t('generatorCard.scale'), id: UNIT_IDS.MW },
            { value: t('generatorCard.scale3'), id: UNIT_IDS.KW },
        ],
        [t],
    )

    const { renewableEnergyType, totalPowerFrom, totalPowerTo, qualificDate } = values

    const isLoading = useAppSelector((state) => state.dictionaries.loading)

    const dispatch = useAppDispatch()
    useEffect(() => {
        if (isLoading !== ELoading.Loaded) {
            dispatch(getDictionaries())
        }
    }, [])

    const [powerMeasurement, setPowerMeasurement] = useState<string>(UNIT_IDS.MW)

    const dictionaries = useAppSelector(selectDictionariesOptions)

    if (!dictionaries) {
        return <CenteredSpin />
    }

    const { renewableEnergyTypesOptions } = dictionaries

    const displayPowerMeasurement =
        unitTypes.find((unit) => unit.id === powerMeasurement)?.value || unitTypes[0]?.value

    const realTotalPowerFrom =
        powerMeasurement === UNIT_IDS.MW ? totalPowerFrom / 1000 : totalPowerFrom
    const realTotalPowerTo = powerMeasurement === UNIT_IDS.MW ? totalPowerTo / 1000 : totalPowerTo

    return (
        <div className={style.filtersMain}>
            <div>{t('filters.powerType')}</div>
            <TagSelector
                value={renewableEnergyType}
                onChange={(renewableEnergyType) =>
                    onChange({
                        ...values,
                        renewableEnergyType,
                    })
                }
                customStyle={{ marginTop: 0 }}
                options={renewableEnergyTypesOptions}
                showSearch={false}
                currentLang={locale}
            />
            <div className={style.filterTitle}>{titles?.power || t('ownerCard.sumPower')}</div>
            <DropdownSelector
                value={displayPowerMeasurement}
                onChange={(selectedValue) => {
                    const selectedUnit = unitTypes.find((unit) => unit.value === selectedValue)
                    if (selectedUnit) {
                        setPowerMeasurement(selectedUnit.id)
                    }
                }}
                options={unitTypes}
                customStyle={{ width: '100%' }}
            />
            <div className={style.inputWrapper}>
                <Input
                    value={realTotalPowerFrom}
                    onChange={(e) => {
                        const value = Math.max(+e.target.value || 0, 0)
                        onChange({
                            ...values,
                            totalPowerFrom: powerMeasurement === UNIT_IDS.MW ? value * 1000 : value,
                        })
                    }}
                    className={style.input}
                />
                <Input
                    value={realTotalPowerTo}
                    onChange={(e) => {
                        const value = Math.max(+e.target.value || 0, 0)
                        onChange({
                            ...values,
                            totalPowerTo: powerMeasurement === UNIT_IDS.MW ? value * 1000 : value,
                        })
                    }}
                    className={style.input}
                />
            </div>
            {showQualificDate && (
                <>
                    <div className={style.filterTitle}>{names?.rangePickerTitle || ''}</div>
                    <div>
                        <TimeRangePicker
                            onFocus={() => disableOutside && disableOutside(true)}
                            onBlur={() => disableOutside && disableOutside(false)}
                            style={{ width: '100%' }}
                            value={qualificDate}
                            onlyBeforeDateAndToday={moment()}
                            onChange={(e) => {
                                onChange({
                                    ...values,
                                    qualificDate: e || [null, null],
                                })
                            }}
                        />
                    </div>
                </>
            )}
            <div className={style.resetButton}>
                <IconButton
                    onClick={() => {
                        onChange({
                            renewableEnergyType: [],
                            totalPowerFrom: 0,
                            totalPowerTo: 0,
                        })
                        setPowerMeasurement(UNIT_IDS.MW)
                        onClose && onClose()
                    }}
                >
                    {t('ownerCard.resetFilters')}
                </IconButton>
            </div>
        </div>
    )
}

export default Filters
